import {
  loadTokensToReduxAsync,
  loadTokensToReduxSync,
} from './localStorageManager';
var axios = require('axios');
var { subscribe } = require('redux-subscriber');
var authenticateActions = require('@actions/authenticateActions');
var store = require('@store/configureStore').configure();
var { error_message } = require('@managers/helpers');
const daysjs = require('../utils/dayjsConfig');
var {
  getItem,
  removeItem,
  setItem,
} = require('@managers/localStorageManager.jsx');
var { getMixpanelInstance } = require('@managers/mixpanelManager');

const SCIROOT_REFRESH_TOKEN_PATH = 'refresh_token';

// Refresh token queue mechanism to prevent race conditions
let isRefreshing = false;
let failedRequestsQueue = [];

/**
 * Decode a JWT token to extract its payload
 * @param {string} token - The JWT token
 * @returns {Object|null} - The decoded payload or null if invalid
 */
const decodeJWT = token => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode base64url to base64
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed
    const padding = base64.length % 4;
    if (padding) {
      base64 += '='.repeat(4 - padding);
    }

    // Decode - works in both browser and React Native
    let decoded;
    if (typeof atob === 'function') {
      decoded = atob(base64);
    } else if (typeof Buffer !== 'undefined') {
      decoded = Buffer.from(base64, 'base64').toString('utf-8');
    } else {
      return null;
    }

    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
};

/**
 * Perform token refresh and return the new tokens.
 * The interceptor's `failedRequestsQueue` ensures this is never called
 * concurrently — concurrent 401s are queued, not refreshed in parallel.
 * @param {Object} storeRef - Redux store reference
 * @param {Object} options - Optional configuration
 * @param {string} options.trigger - Label for telemetry (default 'unknown')
 * @returns {Promise<Object>} - Resolves with new tokens or rejects on failure
 */
const performTokenRefresh = (storeRef, options = {}) => {
  const { trigger = 'unknown' } = options;

  isRefreshing = true;

  const state = storeRef.getState();
  const refresh_token = state?.authentication_data?.tokens?.refresh_token;

  if (!refresh_token) {
    isRefreshing = false;
    return Promise.reject(new Error('No refresh token available'));
  }

  const mixpanelInstance = getMixpanelInstance();
  storeRef.dispatch(authenticateActions.initiate_refresh_token());

  return axios
    .post(
      SCIROOT_REFRESH_TOKEN_PATH + '/' + refresh_token,
      {},
      {
        timeout: 30000,
        // Mark this request to skip interceptor handling
        _isRefreshRequest: true,
      }
    )
    .then(res => {
      const tokens = {
        id_token: res.data.id_token,
        access_token: res.data.access_token,
        refresh_token: res.data.refresh_token,
      };

      storeRef.dispatch(
        authenticateActions.refresh_token_success(
          tokens.id_token,
          tokens.access_token,
          tokens.refresh_token
        )
      );

      mixpanelInstance.track('DEBUG_Token Refresh Success', {
        trigger,
      });

      return tokens;
    })
    .catch(err => {
      const errMsg = error_message(err);
      console.error('[AUTH] Token refresh failed:', errMsg);
      mixpanelInstance.track('DEBUG_Token Refresh Failed', {
        err_msg: errMsg,
        status: err?.response?.status,
        trigger,
      });

      storeRef.dispatch(authenticateActions.refresh_token_fail(errMsg));

      throw err;
    })
    .finally(() => {
      isRefreshing = false;
    });
};

const processQueue = (error, token = null) => {
  failedRequestsQueue.forEach(prom => {
    if (error) {
      // Prefer the queued request's own original error so the caller
      // sees what happened to *their* request, not a confusing error
      // from the refresh endpoint. Fall back to `error` if missing.
      prom.reject(prom.originalError ?? error);
    } else {
      prom.resolve(token);
    }
  });
  failedRequestsQueue = [];
};

function updateHeaders(authorization, access_token) {
  axios.defaults.headers.common['Authorization'] = authorization;
  axios.defaults.headers.common['AccessToken'] = access_token;
}

export var clearLocalStorage = reason => {
  // Log when storage is cleared to help debug unexpected logouts
  // Only include stack trace in development to avoid exposing internal paths in production
  const logData = {
    reason: reason || 'unknown',
  };
  if (process.env.NODE_ENV === 'development') {
    logData.stack = new Error().stack?.split('\n').slice(1, 4).join(' <- ');
  }
  console.log('[AUTH] clearLocalStorage called', logData);

  // Track logout event to Mixpanel for remote debugging
  // Skip tracking for intentional user logout (that's already tracked separately)
  if (reason && reason !== 'user_logout') {
    try {
      const mixpanelInstance = getMixpanelInstance();
      mixpanelInstance.track('DEBUG_Session Cleared', {
        reason: reason,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      // Ignore mixpanel errors
    }
  }

  return Promise.all([
    removeItem('ID_TOKEN'),
    removeItem('ACCESS_TOKEN'),
    removeItem('REFRESH_TOKEN'),
    removeItem('USER_DETAILS'),
    removeItem('SELECTED_DISTRIBUTION_ID'),
    removeItem('SELECTED_FARMING_UNIT_ID'),
    removeItem('SELECTED_FIELD_ID'),
    removeItem('SELECTED_SENSOR_ID'),
    removeItem('DISTRIBUTIONS_DATA'),
    removeItem('TT_SELECTED_DISTRIBUTION_ID'),
    removeItem('TT_SELECTED_FARMING_UNIT_ID'),
    removeItem('TT_SELECTED_FIELD_ID'),
    removeItem('HISTORY_LOG_DATA'),
    removeItem('TT_SEARCH_TYPE'),
  ]);
};

export var getUserDetails = () => {
  if (
    getItem('ID_TOKEN', '') &&
    getItem('ACCESS_TOKEN', '') &&
    getItem('USER_DETAILS', '')
  ) {
    try {
      const userDetailsString = getItem('USER_DETAILS', '');
      // Ensure we have valid JSON string before parsing
      if (!userDetailsString || userDetailsString.trim() === '') {
        return null;
      }
      let userDetails = JSON.parse(userDetailsString);
      return userDetails;
    } catch (error) {
      console.error('Failed to parse USER_DETAILS:', error);
      // Clear corrupted data
      removeItem('USER_DETAILS');
      return null;
    }
  } else {
    return null;
  }
};

// Async version for React Native (uses AsyncStorage)
export var getUserDetailsAsync = async () => {
  try {
    const idToken = await getItem('ID_TOKEN', '');
    const accessToken = await getItem('ACCESS_TOKEN', '');
    const refreshToken = await getItem('REFRESH_TOKEN', '');
    const userDetailsStr = await getItem('USER_DETAILS', '');

    // Require all 3 tokens to be present (consistent with login_success validation)
    if (idToken && accessToken && refreshToken && userDetailsStr) {
      let userDetails = JSON.parse(userDetailsStr);
      return userDetails;
    }
    return null;
  } catch (error) {
    console.error('Failed to get user details:', error);
    return null;
  }
};

export var initTokensSync = store => {
  loadTokensToReduxSync(store.dispatch);
};

export var initTokensAsync = async store => {
  await loadTokensToReduxAsync(store.dispatch);
};

export var init = (store, baseUrl) => {
  axios.defaults.baseURL = baseUrl;
  // Default 40s timeout for every axios call. Without this, Android can
  // silently kill in-flight TCP sockets when the app backgrounds, leaving
  // promises hanging forever and UI stuck on a spinner. Calls that need
  // a different value (uploads, etc.) override this per-call.
  axios.defaults.timeout = 40000;

  // Subscribe to authentication changes.
  // IMPORTANT: This subscriber only SAVES tokens, never clears them.
  // Clearing happens explicitly in logout() action.
  // This prevents the race condition where subscriber fires on app startup
  // with empty Redux state and clears AsyncStorage before tokens are loaded.
  subscribe('authentication_data.tokens', state => {
    var id_token = state.authentication_data.tokens.id_token;
    var access_token = state.authentication_data.tokens.access_token;
    var refresh_token = state.authentication_data.tokens.refresh_token;

    updateHeaders(id_token, access_token);

    if (id_token && access_token && refresh_token) {
      setItem('ID_TOKEN', id_token);
      setItem('ACCESS_TOKEN', access_token);
      setItem('REFRESH_TOKEN', refresh_token);
    }
  });

  // subscribe to user details changes
  subscribe('authentication_data.user_details', state => {
    var user_details = state.authentication_data.user_details;

    if (user_details) {
      setItem('USER_DETAILS', JSON.stringify(user_details));
    } else {
      removeItem('USER_DETAILS');
    }
  });

  // Add a RESPONSE interceptor
  axios.interceptors.response.use(
    function (response) {
      return response;
    },
    async function (error) {
      const url = error.config?.url || '';
      if (
        url.includes('login') ||
        url.includes('register') ||
        url.includes('forgot_password')
      ) {
        return Promise.reject(error);
      }

      if (error.response) {
        var mixpanelInstance = getMixpanelInstance();
        mixpanelInstance.track('DEBUG_API Call Failed', {
          path: error.response.request?.responseURL,
          err: error.response.status,
          err_msg: error.response.data?.message,
        });

        switch (error.response.status) {
          case 400:
            console.log('400');
            break;
          case 401: {
            console.log('[AUTH] 401 received:', {
              message: error.response.data?.message,
              url: error.config?.url || 'unknown',
            });

            try {
              mixpanelInstance.track('DEBUG_401 Error', {
                url: error.config?.url || 'unknown',
                message: error.response.data?.message,
                timestamp: new Date().toISOString(),
              });
            } catch (e) {
              // Ignore mixpanel errors
            }

            // 401 on the refresh call itself = Cognito explicitly
            // rejected the refresh token. Session is dead, clear
            // storage and dispatch logout — don't try to refresh
            // again (would recurse).
            if (error.config?._isRefreshRequest) {
              await clearLocalStorage('refresh_rejected');
              store.dispatch(authenticateActions.logout_success());
              return Promise.reject(error);
            }

            // For any other 401: try to refresh the token.
            // Don't try to guess the cause from the error message —
            // API Gateway Cognito returns the same "Unauthorized" for
            // expired tokens, invalid tokens, and missing tokens.
            // The refresh attempt itself tells us if the session is recoverable.
            const originalRequest = error.config;

            // Already replayed once with fresh tokens and still 401.
            // Refresh succeeded, so the session is valid as far as
            // Cognito is concerned — this is most likely a transient
            // gateway/cache issue or a backend bug, not session death.
            // Don't refresh again (avoid loop) and don't logout. Let
            // the caller surface the error.
            if (originalRequest._retry) {
              return Promise.reject(error);
            }

            // If another refresh is already in progress, queue this request
            if (isRefreshing) {
              return new Promise((resolve, reject) => {
                failedRequestsQueue.push({
                  resolve,
                  reject,
                  originalError: error,
                });
              })
                .then(tokens => {
                  originalRequest.headers.Authorization = tokens.id_token;
                  originalRequest.headers.AccessToken = tokens.access_token;
                  originalRequest._retry = true;
                  return axios.request(originalRequest);
                })
                .catch(err => Promise.reject(err));
            }

            // Try to refresh
            try {
              const tokens = await performTokenRefresh(store, {
                trigger: 'reactive_401',
              });
              processQueue(null, tokens);

              originalRequest.headers.Authorization = tokens.id_token;
              originalRequest.headers.AccessToken = tokens.access_token;
              originalRequest._retry = true;

              return axios.request(originalRequest);
            } catch (refreshError) {
              console.error(
                '[AUTH] Refresh failed:',
                error_message(refreshError)
              );
              processQueue(refreshError, null);
              // Surface the caller's original 401 — not the refresh
              // error — so the caller sees what happened to *their*
              // request. Terminal-401 cleanup (clear storage, logout)
              // already happened in the _isRefreshRequest branch.
              return Promise.reject(error);
            }
          }
          default:
        }
      }

      return Promise.reject(error);
    }
  );
};

export function validateEmail(email) {
  const re =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

/**
 * Get comprehensive auth debug information.
 * Call from browser console: require('@managers/authenticationManager').getAuthDebugInfo()
 */
export function getAuthDebugInfo() {
  const hasIdToken = !!getItem('ID_TOKEN', '');
  const hasAccessToken = !!getItem('ACCESS_TOKEN', '');
  const hasRefreshToken = !!getItem('REFRESH_TOKEN', '');
  const hasUserDetails = !!getItem('USER_DETAILS', '');

  // Check token expiration
  const accessToken = getItem('ACCESS_TOKEN', '');
  let tokenInfo = null;
  if (accessToken) {
    const payload = decodeJWT(accessToken);
    if (payload?.exp) {
      const expiresAt = new Date(payload.exp * 1000);
      const expiresIn = payload.exp * 1000 - Date.now();
      tokenInfo = {
        expiresAt: expiresAt.toISOString(),
        expiresInMinutes: Math.round(expiresIn / 1000 / 60),
        isExpired: expiresIn <= 0,
      };
    }
  }

  return {
    storage: {
      hasIdToken,
      hasAccessToken,
      hasRefreshToken,
      hasUserDetails,
    },
    tokenInfo,
  };
}
